import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, LineChart, Line, PieChart, Pie, ScatterChart, Scatter,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import './PaperData.css';

const API_BASE = 'https://localhost:7161/api/PaperData';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

const PaperData = () => {
    const [activeTab, setActiveTab] = useState('sota');
    const [loading, setLoading] = useState(false);

    const [sotaData, setSotaData] = useState(null);
    const [clusterData, setClusterData] = useState(null);
    const [techScoreDoc, setTechScoreDoc] = useState(null);
    const [vizData, setVizData] = useState(null);

    const tabs = [
        { id: 'sota', label: 'SOTA Comparison', icon: '📊' },
        { id: 'clusters', label: 'Per-Cluster Analysis', icon: '📈' },
        { id: 'techscore', label: 'TechImpactScore Formula', icon: '📐' },
        { id: 'visualizations', label: 'Visualizations', icon: '📉' }
    ];

    useEffect(() => {
        loadData(activeTab);
    }, [activeTab]);

    const loadData = async (tab) => {
        setLoading(true);
        try {
            switch (tab) {
                case 'sota':
                    if (!sotaData) {
                        const response = await axios.get(`${API_BASE}/sota-comparison`);
                        setSotaData(response.data.result);
                    }
                    break;
                case 'clusters':
                    if (!clusterData) {
                        const response = await axios.get(`${API_BASE}/per-cluster-analysis`);
                        setClusterData(response.data.result);
                    }
                    break;
                case 'techscore':
                    if (!techScoreDoc) {
                        const response = await axios.get(`${API_BASE}/techimpactscore-documentation`);
                        setTechScoreDoc(response.data.result);
                    }
                    break;
                case 'visualizations':
                    if (!vizData) {
                        const response = await axios.get(`${API_BASE}/visualization-data`);
                        setVizData(response.data.result);
                    }
                    break;
                default:
                    break;
            }
        } catch (error) {
            console.error('Error loading data:', error);
            alert('Failed to load data. Please ensure the backend is running.');
        } finally {
            setLoading(false);
        }
    };

    const renderSOTAComparison = () => {
        if (!sotaData || !sotaData.datasetInfo) return <div className="loading">Loading SOTA comparison...</div>;

        return (
            <div className="sota-section">
                <h2>🎯 State-of-the-Art Comparison</h2>

                {}
                <div className="info-card">
                    <h3>Dataset Overview</h3>
                    <div className="info-grid">
                        <div><strong>Total Transactions:</strong> {sotaData.datasetInfo.totalTransactions}</div>
                        <div><strong>Time Span:</strong> {sotaData.datasetInfo.timeSpan}</div>
                        <div><strong>Years Covered:</strong> {sotaData.datasetInfo.yearsCovered}</div>
                        <div><strong>Data Completeness:</strong> {sotaData.datasetInfo.dataCompleteness}%</div>
                    </div>
                </div>

                {}
                <div className="table-card">
                    <h3>Algorithm Performance Comparison</h3>
                    <div className="table-wrapper">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Algorithm</th>
                                    <th>Type</th>
                                    <th>Silhouette ↑</th>
                                    <th>Davies-Bouldin ↓</th>
                                    <th>Clusters</th>
                                    <th>Noise %</th>
                                    <th>Verdict</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sotaData.algorithmComparison.map((alg, idx) => (
                                    <tr key={idx} className={alg.rank === 1 ? 'best-algorithm' : ''}>
                                        <td className="rank">{alg.rank === 1 ? '🥇' : alg.rank === 2 ? '🥈' : alg.rank === 3 ? '🥉' : alg.rank}</td>
                                        <td className="algorithm-name">{alg.algorithm}</td>
                                        <td>{alg.type}</td>
                                        <td className="metric-good">{alg.silhouette}</td>
                                        <td className="metric-good">{alg.daviesBouldin}</td>
                                        <td>{alg.numClusters}</td>
                                        <td>{alg.noisePercent}%</td>
                                        <td className={`verdict ${alg.rank === 1 ? 'recommended' : ''}`}>{alg.verdict}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {}
                <div className="chart-card">
                    <h3>Algorithm Performance Visualization</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={sotaData.algorithmComparison}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="algorithm" angle={-15} textAnchor="end" height={80} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="silhouette" fill="#8884d8" name="Silhouette Score" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {}
                <div className="findings-card">
                    <h3>🔍 Key Findings for Paper</h3>
                    <ul className="findings-list">
                        {sotaData.keyFindings.map((finding, idx) => (
                            <li key={idx}>{finding}</li>
                        ))}
                    </ul>
                </div>

                {}
                <div className="table-card">
                    <h3>Comparison with Prior Work (Related Work Section)</h3>
                    <div className="table-wrapper">
                        <table className="prior-work-table">
                            <thead>
                                <tr>
                                    <th>Study</th>
                                    <th>Method</th>
                                    <th>Limitation</th>
                                    <th>Our Improvement</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sotaData.comparisonWithPriorWork.map((work, idx) => (
                                    <tr key={idx}>
                                        <td><strong>{work.study}</strong></td>
                                        <td>{work.method}</td>
                                        <td className="limitation">{work.limitation}</td>
                                        <td className="improvement">{work.ourImprovement}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {}
                <div className="info-card">
                    <h3>Statistical Significance Tests</h3>
                    <div className="stats-grid">
                        <div>
                            <strong>ANOVA p-value:</strong> {sotaData.statisticalTests.anovA_p_value}
                        </div>
                        <div>
                            <strong>Kruskal-Wallis p-value:</strong> {sotaData.statisticalTests.kruskalWallis_p_value}
                        </div>
                        <div>
                            <strong>Effect Size:</strong> {sotaData.statisticalTests.effectSize}
                        </div>
                        <div className="full-width">
                            <strong>Interpretation:</strong> {sotaData.statisticalTests.description}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderClusterAnalysis = () => {
        if (!clusterData || !clusterData.clusterStatistics) return <div className="loading">Loading cluster analysis...</div>;

        return (
            <div className="cluster-section">
                <h2>📈 Per-Cluster Statistical Analysis</h2>

                {/* Overall Metrics */}
                <div className="info-card">
                    <h3>Overall Clustering Metrics (Reverse Hybrid - Best Algorithm)</h3>
                    <div className="info-grid">
                        <div><strong>Algorithm:</strong> {clusterData.algorithm}</div>
                        <div><strong>Total Clusters:</strong> {clusterData.overallMetrics.totalClusters}</div>
                        <div><strong>Silhouette Score:</strong> <span className="metric-good">{clusterData.overallMetrics.overallSilhouette?.toFixed(4)}</span></div>
                        <div><strong>Davies-Bouldin:</strong> <span className="metric-good">{clusterData.overallMetrics.overallDaviesBouldin?.toFixed(4)}</span></div>
                        <div><strong>Noise %:</strong> {clusterData.overallMetrics.noisePercentage?.toFixed(2)}%</div>
                        <div><strong>Total Transactions:</strong> {clusterData.overallMetrics.totalTransactions}</div>
                    </div>
                </div>

                {/* Cluster Size Distribution */}
                <div className="chart-card">
                    <h3>Cluster Size Distribution</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={clusterData.clusterStatistics}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={(entry) => `${entry.clusterName} (${entry.percentOfTotal}%)`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="size"
                            >
                                {clusterData.clusterStatistics.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Detailed Cluster Statistics */}
                {clusterData.clusterStatistics.map((cluster, idx) => (
                    <div key={idx} className="cluster-detail-card">
                        <h3 style={{ color: COLORS[idx % COLORS.length] }}>
                            {cluster.clusterName} ({cluster.clusterId})
                        </h3>

                        <div className="cluster-meta">
                            <span className="badge">Size: {cluster.size} ({cluster.percentOfTotal}%)</span>
                            <span className="badge">Duration: {Math.round(cluster.durationDays)} days</span>
                            <span className="badge">{cluster.dateRange}</span>
                        </div>

                        <p className="historical-context"><strong>Historical Context:</strong> {cluster.historicalContext}</p>

                        {/* Statistics Grid */}
                        <div className="stats-comparison">
                            <div className="stat-box">
                                <h4>Lifetime Statistics</h4>
                                <p><strong>Mean:</strong> {cluster.lifetimeStats.mean} years</p>
                                <p><strong>Std Dev:</strong> {cluster.lifetimeStats.stdDev}</p>
                                <p><strong>Range:</strong> {cluster.lifetimeStats.min} - {cluster.lifetimeStats.max}</p>
                                <p><strong>Median:</strong> {cluster.lifetimeStats.median}</p>
                            </div>
                            <div className="stat-box">
                                <h4>TechImpact Statistics</h4>
                                <p><strong>Mean:</strong> {cluster.techScoreStats.mean}</p>
                                <p><strong>Std Dev:</strong> {cluster.techScoreStats.stdDev}</p>
                                <p><strong>Range:</strong> {cluster.techScoreStats.min} - {cluster.techScoreStats.max}</p>
                                <p><strong>Median:</strong> {cluster.techScoreStats.median}</p>
                            </div>
                            {cluster.dealValueStats && (
                                <div className="stat-box">
                                    <h4>Deal Value Statistics</h4>
                                    <p><strong>Mean:</strong> ${cluster.dealValueStats.mean}M</p>
                                    <p><strong>Std Dev:</strong> ${cluster.dealValueStats.stdDev}M</p>
                                    <p><strong>Total:</strong> ${cluster.dealValueStats.totalValue}M</p>
                                </div>
                            )}
                        </div>

                        {/* Business Model Distribution */}
                        <div className="business-models">
                            <h4>Business Model Distribution</h4>
                            <div className="model-chips">
                                {cluster.businessModelDistribution.map((model, midx) => (
                                    <span key={midx} className="model-chip">
                                        {model.model}: {model.count} ({model.percent}%)
                                    </span>
                                ))}
                            </div>
                        </div>

                        {/* Top Transactions */}
                        <div className="top-transactions">
                            <h4>Top 5 Notable Transactions</h4>
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Acquirer</th>
                                        <th>Date</th>
                                        <th>Tech Score</th>
                                        <th>Deal Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cluster.topTransactions.map((trans, tidx) => (
                                        <tr key={tidx}>
                                            <td>{trans.company}</td>
                                            <td>{trans.acquirer}</td>
                                            <td>{trans.date}</td>
                                            <td><span className="tech-score">{trans.techScore.toFixed(2)}</span></td>
                                            <td>{trans.dealValue}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ))}

                {/* Effect Sizes (Cohen's d) */}
                <div className="effect-sizes-card">
                    <h3>📊 Effect Sizes Between Clusters (Cohen's d)</h3>
                    <p className="subtitle">Large effect sizes validate distinct M&A waves</p>
                    <div className="table-wrapper">
                        <table className="effect-sizes-table">
                            <thead>
                                <tr>
                                    <th>Comparison</th>
                                    <th>Lifetime Cohen's d</th>
                                    <th>Interpretation</th>
                                    <th>TechScore Cohen's d</th>
                                    <th>Interpretation</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clusterData.effectSizes.map((effect, idx) => (
                                    <tr key={idx}>
                                        <td><strong>{effect.comparison}</strong></td>
                                        <td className={`effect-${effect.lifetimeEffectSize.interpretation.toLowerCase()}`}>
                                            {effect.lifetimeEffectSize.cohensD}
                                        </td>
                                        <td>{effect.lifetimeEffectSize.interpretation}</td>
                                        <td className={`effect-${effect.techScoreEffectSize.interpretation.toLowerCase()}`}>
                                            {effect.techScoreEffectSize.cohensD}
                                        </td>
                                        <td>{effect.techScoreEffectSize.interpretation}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="interpretation-guide">
                        <p><strong>Interpretation Guide:</strong> {clusterData.interpretationGuide.effectSizeInterpretation}</p>
                    </div>
                </div>
            </div>
        );
    };

    const renderTechScoreDoc = () => {
        if (!techScoreDoc || !techScoreDoc.formula) return <div className="loading">Loading TechImpactScore documentation...</div>;

        return (
            <div className="techscore-section">
                <h2>📐 TechImpactScore: Objective Formula Documentation</h2>

                {/* Formula Overview */}
                <div className="formula-card">
                    <h3>{techScoreDoc.title}</h3>
                    <p><strong>Purpose:</strong> {techScoreDoc.purpose}</p>

                    <div className="formula-box">
                        <h4>Formula</h4>
                        <p className="formula-equation">{techScoreDoc.formula.equation}</p>
                        <p><em>{techScoreDoc.formula.justification}</em></p>
                    </div>

                    <div className="weights-visual">
                        <h4>Component Weights</h4>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                                <Pie
                                    data={[
                                        { name: 'Patents', value: techScoreDoc.formula.weights.patents },
                                        { name: 'Deal Value', value: techScoreDoc.formula.weights.dealValue },
                                        { name: 'Company Age', value: techScoreDoc.formula.weights.companyAge },
                                        { name: 'Acquirer Activity', value: techScoreDoc.formula.weights.acquirerActivity }
                                    ]}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={true}
                                    label={(entry) => `${entry.name}: ${(entry.value * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {[0, 1, 2, 3].map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Components Detailed */}
                <div className="components-grid">
                    {techScoreDoc.components.map((comp, idx) => (
                        <div key={idx} className="component-card" style={{ borderLeftColor: COLORS[idx] }}>
                            <h4>{comp.name} <span className="weight">({(comp.weight * 100)}%)</span></h4>
                            <p className="component-desc">{comp.description}</p>
                            <p><strong>Calculation:</strong> {comp.calculation}</p>
                            <p><strong>Data Source:</strong> {comp.dataSource}</p>
                            <p><strong>Rationale:</strong> {comp.rationale}</p>
                        </div>
                    ))}
                </div>

                {/* Validation Study */}
                <div className="validation-card">
                    <h3>✅ Validation Study Results</h3>
                    <div className="validation-grid">
                        <div className="validation-item">
                            <h4>Inter-Rater Reliability</h4>
                            <p>{techScoreDoc.validationStudy.interRaterReliability}</p>
                        </div>
                        <div className="validation-item">
                            <h4>Expert Correlation</h4>
                            <p>{techScoreDoc.validationStudy.correlation_with_Expert_Ratings}</p>
                        </div>
                        <div className="validation-item">
                            <h4>Predictive Validity</h4>
                            <p>{techScoreDoc.validationStudy.predictive_Validity}</p>
                        </div>
                        <div className="validation-item">
                            <h4>Robustness</h4>
                            <p>{techScoreDoc.validationStudy.robustness}</p>
                        </div>
                    </div>
                </div>

                {/* Example Calculation */}
                <div className="example-card">
                    <h3>📝 Example Calculation</h3>
                    <p><strong>Company:</strong> {techScoreDoc.exampleCalculation.company}</p>

                    <div className="example-inputs">
                        <div>Patents: {techScoreDoc.exampleCalculation.patents}</div>
                        <div>Deal Value: ${(techScoreDoc.exampleCalculation.dealValue / 1_000_000).toFixed(1)}M</div>
                        <div>Company Lifetime: {techScoreDoc.exampleCalculation.companyLifetime} years</div>
                        <div>Acquirer Deals: {techScoreDoc.exampleCalculation.acquirerDeals}</div>
                    </div>

                    <div className="calculation-steps">
                        <h4>Calculation Steps:</h4>
                        <ol>
                            {techScoreDoc.exampleCalculation.steps.map((step, idx) => (
                                <li key={idx}>{step}</li>
                            ))}
                        </ol>
                    </div>

                    <div className="final-score">
                        <h4>Final TechImpactScore: <span className="score-value">{techScoreDoc.exampleCalculation.finalScore}</span></h4>
                        <p>{techScoreDoc.exampleCalculation.interpretation}</p>
                    </div>
                </div>

                {/* Advantages */}
                <div className="advantages-card">
                    <h3>✨ Advantages Over Subjective Methods</h3>
                    <ul className="advantages-list">
                        {techScoreDoc.advantagesOverSubjectiveMethods.map((adv, idx) => (
                            <li key={idx}>{adv}</li>
                        ))}
                    </ul>
                </div>

                {/* Limitations & Future Work */}
                <div className="future-work-section">
                    <div className="limitations-card">
                        <h4>Limitations</h4>
                        <ul>
                            {techScoreDoc.limitations.map((lim, idx) => (
                                <li key={idx}>{lim}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="future-card">
                        <h4>Future Work</h4>
                        <ul>
                            {techScoreDoc.futureWork.map((work, idx) => (
                                <li key={idx}>{work}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const renderVisualizations = () => {
        if (!vizData || !vizData.figure1_TimelineDensity) return <div className="loading">Loading visualization data...</div>;

        return (
            <div className="visualizations-section">
                <h2>📉 Paper Visualizations (8 Figures)</h2>

                {/* Figure 1: Timeline */}
                <div className="figure-card">
                    <h3>Figure 1: M&A Transaction Timeline (1975-2025)</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure1}</p>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={vizData.figure1_TimelineDensity}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="year" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="count" stroke="#8884d8" name="Transaction Count" strokeWidth={2} />
                            <Line yAxisId="right" type="monotone" dataKey="dealValueTotal" stroke="#82ca9d" name="Deal Value ($M)" strokeWidth={2} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                {/* Figure 2: Correlations */}
                <div className="figure-card">
                    <h3>Figure 2: Feature Correlation Matrix</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure2}</p>
                    <div className="correlation-matrix">
                        <table className="heatmap-table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Date</th>
                                    <th>Lifetime</th>
                                    <th>TechScore</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <th>Date</th>
                                    <td className="corr-high">1.000</td>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.date_Lifetime)}`}>
                                        {vizData.figure2_FeatureCorrelations.date_Lifetime}
                                    </td>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.date_TechScore)}`}>
                                        {vizData.figure2_FeatureCorrelations.date_TechScore}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Lifetime</th>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.date_Lifetime)}`}>
                                        {vizData.figure2_FeatureCorrelations.date_Lifetime}
                                    </td>
                                    <td className="corr-high">1.000</td>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.lifetime_TechScore)}`}>
                                        {vizData.figure2_FeatureCorrelations.lifetime_TechScore}
                                    </td>
                                </tr>
                                <tr>
                                    <th>TechScore</th>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.date_TechScore)}`}>
                                        {vizData.figure2_FeatureCorrelations.date_TechScore}
                                    </td>
                                    <td className={`corr-${getCorrelationClass(vizData.figure2_FeatureCorrelations.lifetime_TechScore)}`}>
                                        {vizData.figure2_FeatureCorrelations.lifetime_TechScore}
                                    </td>
                                    <td className="corr-high">1.000</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Figure 4: Elbow Method */}
                <div className="figure-card">
                    <h3>Figure 4: Elbow Method for K Selection</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure4}</p>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={vizData.figure4_ElbowMethod}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="k" label={{ value: 'Number of Clusters (K)', position: 'insideBottom', offset: -5 }} />
                            <YAxis label={{ value: 'WCSS', angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="wcss" stroke="#8884d8" strokeWidth={3}
                                  dot={(props) => {
                                      const { cx, cy, payload } = props;
                                      if (payload.isElbow) {
                                          return <circle cx={cx} cy={cy} r={8} fill="red" stroke="red" strokeWidth={2} />;
                                      }
                                      return <circle cx={cx} cy={cy} r={4} fill="#8884d8" />;
                                  }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    <p className="elbow-note">⭐ Red dot indicates chosen K=5 (elbow point - matches paper Section 3.3)</p>
                </div>

                {/* Figure 5: Algorithm Comparison */}
                <div className="figure-card">
                    <h3>Figure 5: Algorithm Performance Comparison</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure5}</p>
                    <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={vizData.figure5_AlgorithmComparison}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="algorithm" angle={-15} textAnchor="end" height={100} />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Bar yAxisId="left" dataKey="silhouette" fill="#8884d8" name="Silhouette Score" />
                            <Bar yAxisId="right" dataKey="daviesBouldin" fill="#82ca9d" name="Davies-Bouldin Index" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Figure 6: Cluster Distribution */}
                <div className="figure-card">
                    <h3>Figure 6: Final Cluster Distribution (Reverse Hybrid)</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure6}</p>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={vizData.figure6_ClusterDistribution}
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                label={(entry) => `${entry.clusterId}: ${entry.size} (${entry.dateRange})`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="size"
                            >
                                {vizData.figure6_ClusterDistribution.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Figure 8: Data Quality */}
                <div className="figure-card">
                    <h3>Figure 8: Data Completeness Metrics</h3>
                    <p className="figure-instruction">{vizData.plottingInstructions.figure8}</p>
                    <div className="data-quality-grid">
                        <div className="quality-stat">
                            <h4>{vizData.figure8_DataQualityMetrics.totalTransactions}</h4>
                            <p>Total Transactions</p>
                        </div>
                        <div className="quality-stat">
                            <h4>{vizData.figure8_DataQualityMetrics.withDealValue}</h4>
                            <p>With Deal Value</p>
                        </div>
                        <div className="quality-stat">
                            <h4>{vizData.figure8_DataQualityMetrics.withPatentData}</h4>
                            <p>With Patent Data</p>
                        </div>
                        <div className="quality-stat">
                            <h4>{vizData.figure8_DataQualityMetrics.completenessPercent}%</h4>
                            <p>Completeness</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const getCorrelationClass = (value) => {
        const abs = Math.abs(value);
        if (abs > 0.7) return 'high';
        if (abs > 0.4) return 'medium';
        return 'low';
    };

    return (
        <div className="paper-data-container">
            <div className="paper-header">
                <h1>📄 Paper Data Dashboard</h1>
                <p className="paper-subtitle">
                    Comprehensive M&amp;A Data Analysis
                </p>
            </div>

            <div className="tabs-container">
                {tabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.id)}
                    >
                        <span className="tab-icon">{tab.icon}</span>
                        <span className="tab-label">{tab.label}</span>
                    </button>
                ))}
            </div>

            <div className="content-area">
                {loading ? (
                    <div className="loading-spinner">
                        <div className="spinner"></div>
                        <p>Loading data...</p>
                    </div>
                ) : (
                    <>
                        {activeTab === 'sota' && renderSOTAComparison()}
                        {activeTab === 'clusters' && renderClusterAnalysis()}
                        {activeTab === 'techscore' && renderTechScoreDoc()}
                        {activeTab === 'visualizations' && renderVisualizations()}
                    </>
                )}
            </div>

        </div>
    );
};

export default PaperData;
